import React, { useCallback, useMemo } from 'react'
import TableComp from 'src/components/common/table/Table'
import ActionIcon from 'src/assets/icons/table-action.svg?react'
import { TableProps } from 'antd'
import { ExpertTableDataType } from 'src/types/api.types'
import { AdditionalExpertsTableColumn, ExpertsTableColumn } from './TableColumn'
import { TagType } from 'src/types/expert.types'
import { DataTestId } from 'src/constants/ui.constants'
import SegmentAnalytics from 'src/SegmentAnalytics'

export type TableCompProps = {
    dataSource?: ExpertTableDataType[]
    isLoading: boolean
    isFilterVisible: boolean
    pagination: { page: number; pageSize: number }
    total: number
    onUpdatePagination: (page: number, pageSize: number) => void
    rowSelection: TableProps<ExpertTableDataType>['rowSelection']
    setIsModalOpen: (id: string) => void
    emptyTableText?: string
    isSidebarCollapsed: boolean
}

export const ExpertsTable: React.FC<TableCompProps> = ({
    dataSource,
    isLoading,
    isFilterVisible,
    pagination,
    total,
    onUpdatePagination,
    rowSelection,
    setIsModalOpen,
    emptyTableText,
    isSidebarCollapsed,
}) => {
    const handleActionModal = useCallback(
        (id: string) => {
            SegmentAnalytics.sendTrackEvent('Table_ActionProfileCTA_Clicked', {
                expertId: id,
            })
            setIsModalOpen(id)
        },
        [setIsModalOpen]
    )

    const columns = useMemo(() => {
        const columns = ExpertsTableColumn.map(column => {
            return column.key === 'actions'
                ? {
                      ...column,
                      render: (_: unknown, record: ExpertTableDataType) => {
                          return <ActionIcon onClick={() => handleActionModal(record.key)} />
                      },
                  }
                : column
        })
        if (isSidebarCollapsed) {
            const skillsIndex = columns.findIndex(column => column.key === TagType.skills)
            if (skillsIndex !== -1) {
                columns.splice(skillsIndex + 1, 0, ...AdditionalExpertsTableColumn)
            }
        }
        return columns
    }, [isSidebarCollapsed, handleActionModal])

    const getTableContentHeight = useCallback(
        (isEmpty = false) => {
            const baseHeight = isFilterVisible ? 'calc(100vh - 400px)' : 'calc(100vh - 306px)'
            return isEmpty ? `calc(${baseHeight} - 34px)` : baseHeight
        },
        [isFilterVisible]
    )

    return (
        <div data-testid={DataTestId.ExpertsTable}>
            <TableComp<ExpertTableDataType>
                loading={isLoading}
                columns={columns}
                dataSource={dataSource || []}
                size="middle"
                emptyIconText={emptyTableText}
                pagination={{
                    pageSizeOptions: ['10', '20', '50', '100'],
                    hideOnSinglePage: false,
                    total,
                    showSizeChanger: true,
                    position: ['bottomCenter'],
                    current: pagination.page,
                    pageSize: pagination.pageSize,
                    onChange: onUpdatePagination,
                }}
                scroll={{
                    y: getTableContentHeight(),
                    x: '1120px',
                }}
                rowSelection={rowSelection}
                emptyTableHeight={getTableContentHeight(true)}
            />
        </div>
    )
}
